import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { DropdownItem, RowSelectionState, M, Button } from 'design-system-web';
import { bulkAssignTaskMemberMutation } from 'graphql-queries';
import { Dropdown } from 'components';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Assignable } from 'domains-work-order-type-contracts';

const TASK_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    assignableUsers: Assignable[];
    refetch?: () => void;
};

export function BulkAssigneeAction({ rowSelection, assignableUsers, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkAssignTaskMember] = useMutation(bulkAssignTaskMemberMutation);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<Assignable | null>(null);
    const [loading, setLoading] = useState(false);

    const selectedTaskIds = useMemo(() => {
        if (!rowSelection) {
            return [];
        }

        return Object.entries(rowSelection)
            .filter(([, isSelected]) => isSelected)
            .map(([taskId]) => taskId);
    }, [rowSelection]);

    const openModal = async () => {
        if (selectedTaskIds.length === 0) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.noSelection')
            );

            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.limitExceeded', {
                    taskSelectionLimit: TASK_SELECTION_LIMIT,
                    selectedTaskCount: selectedTaskIds.length,
                }),
                5000
            );

            return;
        }

        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const handleConfirm = async () => {
        if (!selectedUser) {
            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.limitExceeded', {
                    taskSelectionLimit: TASK_SELECTION_LIMIT,
                    selectedTaskCount: selectedTaskIds.length,
                }),
                5000
            );

            closeModal();

            return;
        }

        setLoading(true);

        try {
            const result = await bulkAssignTaskMember({
                variables: {
                    taskIds: selectedTaskIds,
                    assignee: selectedUser.value,
                },
            });

            const { successCount, failureCount } = result.data?.bulkAssignTaskMember || {
                successCount: 0,
                failureCount: selectedTaskIds.length,
            };

            if (failureCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.success', {
                        count: successCount,
                    })
                );
            } else if (successCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.error')
                );
            } else {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.partial', {
                        successCount,
                        failureCount,
                    }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.error')
            );
        }

        setLoading(false);
        closeModal();
    };

    return (
        <>
            <Button
                variant="secondary"
                size="large"
                onClick={openModal}
                testId="bulk-task-assignee-select"
            >
                {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.selectMember')}
            </Button>

            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                handleConfirm={handleConfirm}
                title={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.title')}
                confirmText={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.confirm.button')}
                cancelText={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.cancel.button')}
                loading={loading}
                confirmActionDisabled={!selectedUser || loading}
                cancelActionDisabled={loading}
                isFullWidth
            >
                <M>
                    {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.modal.prompt', {
                        count: selectedTaskIds.length,
                    })}
                </M>

                <Dropdown
                    isSearchable
                    value={selectedUser?.value || ''}
                    items={assignableUsers || []}
                    onChange={(item: DropdownItem<string>) => {
                        const matchedUser = assignableUsers.find(user => user.value === item.value);

                        if (matchedUser) {
                            setSelectedUser(matchedUser);
                        }
                    }}
                    placeholder={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignMember.selectMember')}
                    doTranslation={false}
                />
            </ConfirmationModal>
        </>
    );
}
